import { useCallback, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { CurrencyCode } from "@shared/money";
import { keypadDigitsToMinor } from "@shared/money";
import { NumberDisplay } from "./primitives";

/**
 * OWNED BY C1. The numeric keypad: the only keyboard in the selling flow (U-004).
 * Digits are entered right-to-left as minor units; `keypadDigitsToMinor` from @shared/money is the single
 * source of truth for the conversion, so JPY "1250" is ¥1250 and USD "1250" is $12.50.
 */

/** The fixed note that must appear on every price keypad (F-302, D-016). */
export const TAX_NOTE = "Prices include any tax. WebCashier does not calculate tax." as const;

export interface KeypadProps {
  /** Controlled digit string, e.g. "1250". */
  digits: string;
  onDigitsChange: (digits: string) => void;
  /** Convenience callback with the parsed minor-unit value. */
  onValueChange?: (minorUnits: number) => void;
  currency: CurrencyCode;
  /** Persistent note under the amount, e.g. TAX_NOTE on price entry. */
  note?: ReactNode;
  /** Label above the amount ("Amount", "Opening float", "Counted cash"). */
  label?: string;
  maxDigits?: number;
  /** Hide the built-in amount readout when the screen shows its own. */
  hideAmount?: boolean;
  size?: "md" | "lg" | "xl";
}

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"] as const;

export function Keypad({
  digits, onDigitsChange, onValueChange, currency, note, label = "Amount", maxDigits = 9, hideAmount, size = "lg",
}: KeypadProps) {
  const emit = useCallback((next: string) => {
    const clean = next.replace(/\D/g, "").replace(/^0+(?=\d)/, "").slice(0, maxDigits);
    onDigitsChange(clean);
    onValueChange?.(keypadDigitsToMinor(clean));
  }, [maxDigits, onDigitsChange, onValueChange]);

  const press = (d: string) => emit(digits + d);

  return (
    <div className="wc-keypad">
      {hideAmount ? null : (
        <NumberDisplay valueMinor={keypadDigitsToMinor(digits)} currency={currency} label={label} size={size} align="right" />
      )}
      {note ? <p className="wc-keypad__note">{note}</p> : null}
      <div className="wc-keypad__grid" role="group" aria-label={`${label} keypad`}>
        {KEYS.map((k) => (
          <button key={k} type="button" className="wc-keypad__key" aria-label={k} onClick={() => press(k)}>{k}</button>
        ))}
        <button type="button" className="wc-keypad__key wc-keypad__key--util" aria-label="Clear" onClick={() => emit("")}>Clear</button>
        <button type="button" className="wc-keypad__key" aria-label="0" onClick={() => press("0")}>0</button>
        <button type="button" className="wc-keypad__key wc-keypad__key--util" aria-label="Backspace"
          onClick={() => emit(digits.slice(0, -1))}>⌫</button>
      </div>
    </div>
  );
}

/** Local keypad state with the minor-unit value already parsed. */
export function useKeypad(initialMinor = 0) {
  const [digits, setDigits] = useState(initialMinor > 0 ? String(initialMinor) : "");
  const minor = useMemo(() => keypadDigitsToMinor(digits), [digits]);
  const reset = useCallback((toMinor = 0) => setDigits(toMinor > 0 ? String(toMinor) : ""), []);
  return { digits, setDigits, minor, reset };
}
